import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useGetVendorProductsQuery,
  useGetVendorCategorySubcategoryMapQuery,
} from "../slices/productApiSlice";
import { useCreateOrderMutation } from "../slices/orderApiSlice";

const SalesOrderScreen = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);

  const { data: products = [] } = useGetVendorProductsQuery();
  const {
    data: categoryMapData,
    error,
    isSuccess,
    isError,
  } = useGetVendorCategorySubcategoryMapQuery();
  console.log(categoryMapData);
  const [createOrder] = useCreateOrderMutation();
  const [salesOrder, setSalesOrder] = useState(null);

  useEffect(() => {
    if (isError) console.error("Error fetching category map:", error);
    if (isSuccess) console.log("Category map received:", categoryMapData);
  }, [isError, isSuccess]);

  const structuredProductMap = useMemo(() => {
    const map = {};
    for (const product of products) {
      const { category, subcategory } = product;
      if (!map[category]) map[category] = {};
      if (!map[category][subcategory]) map[category][subcategory] = [];
      map[category][subcategory].push(product);
    }
    return map;
  }, [products]);

  // Initialize salesOrder
  useEffect(() => {
    if (userInfo?._id) {
      const defaultAddress =
        userInfo.addresses?.[0] || { address: "", city: "", pincode: "", phoneNumber: "" };

      setSalesOrder({
        shippingAddress: { ...defaultAddress },
        products: [
          {
            product: "",
            vendorId: "",
            category: "",
            subCategory: "",
            quantity: 1,
            ProductTotalprice: 0,
            discountApplied: 0,
            discountAmount: 0,
          },
        ],
        OrderTotalprice: 0,
      });
    }
  }, [userInfo]);

  // Update total order price
  useEffect(() => {
    if (!salesOrder) return;
    const total = salesOrder.products.reduce(
      (acc, p) => acc + (p.ProductTotalprice || 0),
      0
    );
    setSalesOrder((prev) => ({ ...prev, OrderTotalprice: total }));
  }, [salesOrder?.products]);

  const handleAddressChange = (field, value) => {
    setSalesOrder((prev) => ({
      ...prev,
      shippingAddress: { ...prev.shippingAddress, [field]: value },
    }));
  };

  const handleProductChange = (index, field, value) => {
  if (!salesOrder) return;

  const updatedProducts = [...salesOrder.products];
  const currentItem = updatedProducts[index];

  if (field === "category") {
    updatedProducts[index] = {
      ...currentItem,
      category: value,
      subCategory: "",
      product: "",
      vendorId: "", // clear vendorId when category changes
      quantity: 1,
      ProductTotalprice: 0,
      discountApplied: 0,
      discountAmount: 0,
    };
  } else if (field === "subCategory") {
    updatedProducts[index] = {
      ...currentItem,
      subCategory: value,
      product: "",
      vendorId: "", // clear vendorId when subcategory changes
      quantity: 1,
      ProductTotalprice: 0,
      discountApplied: 0,
      discountAmount: 0,
    };
  } else if (field === "product") {
    const selectedProduct = products.find((p) => p._id === value);
    if (!selectedProduct) {
      console.error(`Selected product not found: ${value}`);
      return;
    }

    // ✅ Assign vendorId here from product
    updatedProducts[index] = {
      ...currentItem,
      product: selectedProduct._id,
      vendorId: selectedProduct.vendorId,
      discountApplied: selectedProduct.discount || 0,
      quantity: 1,
      ProductTotalprice: Math.max(
        (selectedProduct.shopPrice || 0) - (selectedProduct.discount || 0),
        0
      ),
      discountAmount: selectedProduct.discount || 0,
    };

    console.log(`Product selected at index ${index}:`, updatedProducts[index]);
  } else if (field === "quantity") {
    const quantity = Number(value) || 1;
    const selectedProduct = products.find((p) => p._id === currentItem.product);
    if (!selectedProduct) return;

    const price = selectedProduct.shopPrice || 0;
    const discount = selectedProduct.discount || 0;
    const finalUnitPrice = Math.max(price - discount, 0);

    updatedProducts[index] = {
      ...currentItem,
      quantity,
      ProductTotalprice: finalUnitPrice * quantity,
      discountAmount: discount * quantity,
    };

    console.log(`Quantity updated at index ${index}:`, updatedProducts[index]);
  }

  setSalesOrder({ ...salesOrder, products: updatedProducts });
};



  const addProductLine = () => {
    setSalesOrder((prev) => ({
      ...prev,
      products: [
        ...prev.products,
        {
          product: "",
          vendorId: "",
          category: "",
          subCategory: "",
          quantity: 1,
          ProductTotalprice: 0,
          discountApplied: 0,
          discountAmount: 0,
        },
      ],
    }));
  };

  const removeProductLine = (index) => {
    const updated = [...salesOrder.products];
    updated.splice(index, 1);
    setSalesOrder({ ...salesOrder, products: updated });
  };

  const isDuplicateProduct = (productsArr, idx) => {
    const current = productsArr[idx].product;
    return current && productsArr.filter((p) => p.product === current).length > 1;
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  if (!salesOrder) return;

  const { shippingAddress } = salesOrder;

  // 1️⃣ Shipping validation
  if (!shippingAddress.address || !shippingAddress.city || !shippingAddress.pincode || !shippingAddress.phoneNumber) {
    toast.error("Please fill in complete shipping address");
    return;
  }

  // 2️⃣ Validate selected products
  const validProducts = salesOrder.products.filter(p => p.product && p.quantity > 0);
  if (validProducts.length === 0) {
    toast.error("Please select at least one valid product with quantity > 0");
    return;
  }

  // 3️⃣ Check duplicates
  const hasDuplicates = validProducts.some((_, idx) => validProducts.filter(p => p.product === validProducts[idx].product).length > 1);
  if (hasDuplicates) {
    toast.error("Duplicate products detected.");
    return;
  }

  // 4️⃣ Format products for backend and add vendorId from userInfo
  const formattedProducts = validProducts.map((p) => {
    const productObj = products.find((prod) => prod._id === p.product);
    return {
      vendorId: userInfo._id, // ✅ assign logged-in user
      productId: p.product,
      price: productObj?.shopPrice || 0,
      quantity: p.quantity,
      discount: p.discountApplied * p.quantity,
    };
  });

  console.log("=== Formatted products to send ===");
  console.log(formattedProducts);

  try {
    await createOrder({
      products: formattedProducts,
      shippingAddress,
      paymentStatus: "Paid",
      isShop: true,
    }).unwrap();

    toast.success("Order created successfully!");
    resetOrder();
    navigate("/PendingOrders");
  } catch (err) {
    console.error("Create order failed:", err);
    toast.error(err?.data?.message || "Failed to create order");
  }
};






  const resetOrder = () => {
    const defaultAddress =
      userInfo.addresses?.[0] || { address: "", city: "", pincode: "", phoneNumber: "" };

    setSalesOrder({
      shippingAddress: { ...defaultAddress },
      products: [
        {
          product: "",
          vendorId: "",
          category: "",
          subCategory: "",
          quantity: 1,
          ProductTotalprice: 0,
          discountApplied: 0,
          discountAmount: 0,
        },
      ],
      OrderTotalprice: 0,
    });
  };

  if (!salesOrder) return <div>Loading...</div>;

  return (
    <div className="container my-4">
      <h2 className="text-center mb-4">Create Order</h2>
      <form onSubmit={handleSubmit}>
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">Products</h5>
          </div>
          <div className="card-body">
            {salesOrder.products.map((item, idx) => (
              <ProductLine
                key={idx}
                idx={idx}
                item={item}
                handleProductChange={handleProductChange}
                removeProductLine={removeProductLine}
                isDuplicate={isDuplicateProduct(salesOrder.products, idx)}
                categoryMap={categoryMapData?.categorySubcategoryList || {}}
                structuredProductMap={structuredProductMap}
              />
            ))}
            <button type="button" className="btn btn-outline-success btn-sm" onClick={addProductLine}>
              + Add Product
            </button>
          </div>
        </div>
        <div className="text-end mb-4">
          <h4>
            Order Total Price: <span className="text-success">₹{salesOrder.OrderTotalprice.toFixed(2)}</span>
          </h4>
        </div>
        <div className="text-center">
          <button type="submit" className="btn btn-primary">Submit Order</button>
        </div>
      </form>
    </div>
  );
};

// ---------------- ProductLine Component ----------------
const ProductLine = ({ idx, item, handleProductChange, removeProductLine, isDuplicate, categoryMap, structuredProductMap }) => {
  if (!item) return null;

  const categories = Object.keys(categoryMap || {}).sort((a, b) => a.localeCompare(b));
  const subCategories = [...(categoryMap?.[item.category] || [])].sort((a, b) => a.localeCompare(b));
  const products = structuredProductMap?.[item.category]?.[item.subCategory] || [];
  const sortedProducts = [...products].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="row align-items-end mb-3">
      <div className="col-md-2">
        <label className="form-label">Category</label>
        <select className="form-select" value={item.category} onChange={(e) => handleProductChange(idx, "category", e.target.value)}>
          <option value="">Select</option>
          {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
        </select>
      </div>
      <div className="col-md-2">
        <label className="form-label">Subcategory</label>
        <select className="form-select" value={item.subCategory} onChange={(e) => handleProductChange(idx, "subCategory", e.target.value)} disabled={!item.category}>
          <option value="">Select</option>
          {subCategories.map((subcat) => <option key={subcat} value={subcat}>{subcat}</option>)}
        </select>
      </div>
      <div className="col-md-2">
        <label className="form-label">Product</label>
        <select className={`form-select ${isDuplicate ? "is-invalid" : ""}`} value={item.product} onChange={(e) => handleProductChange(idx, "product", e.target.value)} disabled={!item.subCategory}>
          <option value="">Select</option>
          {sortedProducts.map((prod) => <option key={prod._id} value={prod._id}>{prod.name}</option>)}
        </select>
        {isDuplicate && <div className="invalid-feedback">Duplicate product selected</div>}
      </div>
      <div className="col-md-2">
        <label className="form-label">Quantity</label>
        <div className="d-flex align-items-center">
          <button type="button" className="btn btn-outline-secondary" onClick={() => handleProductChange(idx, "quantity", Math.max(1, item.quantity - 1))} disabled={!item.product}>−</button>
          <div className="mx-2 px-3 py-1 border rounded text-center" style={{ minWidth: "40px", background: "#f8f9fa" }}>{item.quantity}</div>
          <button type="button" className="btn btn-outline-secondary" onClick={() => handleProductChange(idx, "quantity", item.quantity + 1)} disabled={!item.product}>+</button>
        </div>
        <p className="mt-1"><strong>Discount (per unit):</strong> ₹{item.discountApplied}</p>
      </div>
      <div className="col-md-2">
        <p className="mt-4"><strong>Total Price:</strong> ₹{item.ProductTotalprice.toFixed(2)}</p>
      </div>
      <div className="col-md-1 text-end">
        <button type="button" className="btn btn-danger btn-sm mt-4" onClick={() => removeProductLine(idx)}>&times;</button>
      </div>
    </div>
  );
};

export default SalesOrderScreen;
